import { Dayjs, dayjs } from '@dw/dayjs';
import { DATE_FORMATS } from '@dw/constants';
import {
  IMyTeacherNote,
  ITeacherNote,
  ITeacherNoteRecipient,
  ITeacherNoteReply,
  ITeacherNoteThreadSummary,
  IUnifiedTeacherNote,
  IVoiceNote,
  TEACHER_NOTE_REPLY_VISIBILITY,
  TEACHER_NOTE_SOURCE,
} from '@dw/models/teacherNote';

/**
 * Stands in for the words a recording does not have. A voice-only note carries no text at all, so
 * without a token of its own it would fall out of every search that is not blank.
 */
const VOICE_NOTE_SEARCH_TOKEN = 'voice note';

const buildSearchHaystack = (
  note: { text: string; auditUserName: string; replies: ITeacherNoteReply[]; voiceNote?: IVoiceNote },
  recipients?: ITeacherNoteRecipient[]
): string => [
  note.text,
  note.auditUserName,
  ...(recipients ?? []).map(x => x.name),
  ...note.replies.map(x => `${x.auditUserName} ${x.text}`),
  ...(note.voiceNote || note.replies.some(x => x.voiceNote) ? [VOICE_NOTE_SEARCH_TOKEN] : []),
].join(' ').toLowerCase();

/** The fields both arms share verbatim — everything else is derived per representation. */
type TUnifiedNoteBaseFields = Omit<IUnifiedTeacherNote, 'source' | 'recipients' | 'isAuthoredByMe' | 'isGroupNote' | 'searchHaystack'>;

const toBase = (note: IMyTeacherNote | ITeacherNote): TUnifiedNoteBaseFields => ({
  noteId: note.noteId,
  text: note.text,
  attachments: note.attachments,
  voiceNote: note.voiceNote,
  noteDate: note.noteDate,
  createdTimestamp: note.createdTimestamp,
  lastActivityTimestamp: note.lastActivityTimestamp,
  hasUnreadActivity: note.hasUnreadActivity,
  // A cache row written before these existed has neither; fall back to a read, empty state rather
  // than surfacing NaN counts or an undefined thread list.
  threads: note.threads ?? [],
  isNoteUnread: note.isNoteUnread ?? false,
  unreadReplyCount: note.unreadReplyCount ?? 0,
  replies: note.replies,
  authorName: note.auditUserName,
  authorUserId: note.auditUserId,
});

const fromReceived = (note: IMyTeacherNote): IUnifiedTeacherNote => ({
  ...toBase(note),
  source: TEACHER_NOTE_SOURCE.RECEIVED,
  // The caller is a recipient here — an author is never a recipient of their own note.
  isAuthoredByMe: false,
  // A cache row written before `isGroupNote` existed has no value; assume group so the reply
  // audience choice is offered rather than silently withheld.
  isGroupNote: note.isGroupNote ?? true,
  searchHaystack: buildSearchHaystack(note),
});

const fromAuthored = (note: ITeacherNote, currentUserId?: string): IUnifiedTeacherNote => ({
  ...toBase(note),
  source: TEACHER_NOTE_SOURCE.AUTHORED,
  recipients: note.recipients,
  isAuthoredByMe: !!currentUserId && note.auditUserId === currentUserId,
  // The authored arm carries the full list, so derive it rather than trusting a separate flag.
  isGroupNote: note.recipients.length > 1,
  searchHaystack: buildSearchHaystack(note, note.recipients),
});

/**
 * Merge the received and authored lists into one recency-ordered conversation list.
 *
 * A note can appear in both (an admin who is also a recipient of a teacher's note); the
 * participant representation wins because it carries the caller's own seen state and their
 * private reply thread. Note this deliberately trades the observer's all-thread view for the
 * caller's own thread on such notes. Sorted by `lastActivityTimestamp` desc so a reply bumps a
 * note up, chat-style.
 */
export const mergeTeacherNotes = (
  received: IMyTeacherNote[],
  authored: ITeacherNote[],
  currentUserId?: string
): IUnifiedTeacherNote[] => {
  const byNoteId = new Map<string, IUnifiedTeacherNote>();

  for (const note of authored) {
    byNoteId.set(note.noteId, fromAuthored(note, currentUserId));
  }
  for (const note of received) {
    byNoteId.set(note.noteId, fromReceived(note));
  }

  return Array.from(byNoteId.values())
    .sort((a, b) => b.lastActivityTimestamp.valueOf() - a.lastActivityTimestamp.valueOf());
};

export interface ITeacherNoteListFilters {
  searchText: string;
  fromDate?: Dayjs | null;
  toDate?: Dayjs | null;
  participantUserId: string;
}

export const initTeacherNoteListFilters = (): ITeacherNoteListFilters => ({
  searchText: '',
  fromDate: null,
  toDate: null,
  participantUserId: '',
});

export const hasActiveTeacherNoteFilters = (filters: ITeacherNoteListFilters): boolean =>
  !!filters.searchText.trim() || !!filters.fromDate || !!filters.toDate || !!filters.participantUserId;

/** True when the user has explicitly scoped the search by date, replacing the default activity window. */
export const hasExplicitTeacherNoteDateFilter = (filters?: ITeacherNoteListFilters): boolean =>
  !!filters?.fromDate || !!filters?.toDate;

/**
 * Client-side narrowing of the merged list.
 *
 * Dates filter on `noteDate` (when the note was written), matching the server's `fromDate`/`toDate`
 * creation semantics — not on last activity, which is only the sort/window key.
 */
export const filterTeacherNotes = (
  notes: IUnifiedTeacherNote[],
  filters: ITeacherNoteListFilters
): IUnifiedTeacherNote[] => {
  const search = filters.searchText.trim().toLowerCase();

  return notes.filter(note => {
    if (filters.fromDate && note.noteDate.isBefore(filters.fromDate)) {
      return false;
    }
    if (filters.toDate && note.noteDate.isAfter(filters.toDate)) {
      return false;
    }

    if (filters.participantUserId) {
      // The other party is the author on a received note, or one of the recipients on a sent one.
      const isParticipant = note.authorUserId === filters.participantUserId
        || note.recipients?.some(x => x.userId === filters.participantUserId);
      if (!isParticipant) {
        return false;
      }
    }

    if (search && !note.searchHaystack.includes(search)) {
      return false;
    }

    return true;
  });
};

/** A day-worth of notes, most recently active first. */
export interface INoteDayGroup {
  key: string;
  label: string;
  notes: IUnifiedTeacherNote[];
}

/** "Today" / "Yesterday" / "Monday, 3 Jul 2026" — the divider shown above each day's notes. */
const formatNoteDayHeading = (date: Dayjs): string => {
  const dayDiff = dayjs().startOf('day').diff(date.startOf('day'), 'day');
  if (dayDiff === 0) {
    return 'Today';
  }
  if (dayDiff === 1) {
    return 'Yesterday';
  }
  return date.format(DATE_FORMATS.DayMonthYearWithFullDay);
};

/**
 * Group the list into day buckets by last activity, so a note bumped by a reply today appears
 * under Today — keeping the daily mental model coherent with the recency ordering.
 */
export const groupTeacherNotesByDay = (notes: IUnifiedTeacherNote[]): INoteDayGroup[] => {
  const groups = new Map<string, IUnifiedTeacherNote[]>();

  for (const note of notes) {
    const key = note.lastActivityTimestamp.format(DATE_FORMATS.YearMonthDayIso);
    const existing = groups.get(key) ?? [];
    existing.push(note);
    groups.set(key, existing);
  }

  return Array.from(groups.entries())
    .sort(([keyA], [keyB]) => keyB.localeCompare(keyA))
    .map(([key, dayNotes]) => ({
      key,
      label: formatNoteDayHeading(dayNotes[0].lastActivityTimestamp),
      notes: dayNotes,
    }));
};

/** Compact chat-style timestamp: time today, weekday this week, otherwise a short date. */
export const formatCompactActivityTime = (timestamp: Dayjs): string => {
  const dayDiff = dayjs().startOf('day').diff(timestamp.startOf('day'), 'day');
  if (dayDiff === 0) {
    return timestamp.format(DATE_FORMATS.HourMinute);
  }
  if (dayDiff < 7) {
    return timestamp.format(DATE_FORMATS.DayOfWeek);
  }
  return timestamp.format(DATE_FORMATS.DayMonth);
};

/**
 * Identifies the broadcast ("Everyone") thread in the author's thread switcher. Broadcast replies
 * carry no `recipientUserId`, so they need a sentinel key of their own.
 */
export const EVERYONE_THREAD_KEY = '__everyone__';

/** Replies can outlive the recipient they mention, so never render a blank name. */
export const formatReplyAuthorName = (name?: string): string => name?.trim() || 'Former recipient';

/** One selectable conversation within a note — the chip, its counts, and what it posts into. */
export interface ITeacherNoteThreadView {
  key: string;
  label: string;
  isEveryone: boolean;
  /** Absent on the Everyone thread, and on the caller's own thread when the server infers it. */
  recipientUserId?: string;
  replyCount: number;
  unreadReplyCount: number;
  lastActivityTimestamp: Dayjs;
}

/**
 * The note's threads as chips. Labels are role-aware: the shared thread is "Everyone", the
 * caller's own thread reads "Private" (it pairs with the composer's language and describes the
 * visibility rather than naming the reader), and every other thread is its owner's name.
 *
 * Counts come straight from the server rollup, so a chip's badge and count always describe
 * exactly what selecting it renders.
 */
export const buildTeacherNoteThreadViews = (
  threads: ITeacherNoteThreadSummary[],
  currentUserId?: string
): ITeacherNoteThreadView[] => threads
  .map(thread => {
    const isEveryone = !thread.recipientUserId;
    const isOwnThread = !!thread.recipientUserId && thread.recipientUserId === currentUserId;
    return {
      key: thread.recipientUserId ?? EVERYONE_THREAD_KEY,
      label: isEveryone ? 'Everyone' : isOwnThread ? 'Private' : formatReplyAuthorName(thread.name),
      isEveryone,
      recipientUserId: thread.recipientUserId,
      replyCount: thread.replyCount,
      unreadReplyCount: thread.unreadReplyCount,
      lastActivityTimestamp: thread.lastActivityTimestamp,
    };
  })
  // Everyone leads; the rest read alphabetically so the row is stable between polls.
  .sort((a, b) => a.isEveryone === b.isEveryone ? a.label.localeCompare(b.label) : a.isEveryone ? -1 : 1);

/**
 * Which chip to open on. Prefers the first thread with unread replies — the user clicked in to
 * read something — and otherwise falls back to whichever conversation moved most recently.
 */
export const pickDefaultTeacherNoteThreadKey = (views: ITeacherNoteThreadView[]): string | undefined =>
  views.find(x => x.unreadReplyCount > 0)?.key
  ?? [...views].sort((a, b) => b.lastActivityTimestamp.valueOf() - a.lastActivityTimestamp.valueOf())[0]?.key;

/**
 * The replies a thread owns — strictly partitioned, so what a chip counts is what it shows.
 *
 * A reply with no `recipientUserId` belongs to the shared thread (the server coerces it to null on
 * a broadcast), matching how the cache patches in `TeacherNotesApi` assign replies to rollups.
 */
export const filterRepliesForThread = (
  replies: ITeacherNoteReply[],
  view: ITeacherNoteThreadView
): ITeacherNoteReply[] => replies.filter(reply => view.isEveryone
  ? reply.visibility === TEACHER_NOTE_REPLY_VISIBILITY.EVERYONE || !reply.recipientUserId
  : reply.visibility === TEACHER_NOTE_REPLY_VISIBILITY.PRIVATE
    && reply.recipientUserId === view.recipientUserId);

/** Short "To …" summary for a note's recipients — first name plus an overflow count. */
const formatRecipientSummary = (recipients: ITeacherNoteRecipient[]): string => {
  if (recipients.length < 1) {
    return '';
  }
  if (recipients.length === 1) {
    return recipients[0].name;
  }
  return `${recipients[0].name} +${recipients.length - 1}`;
};

/**
 * Who the conversation is with, from the caller's side — the one label shared by the list row
 * and the opened note so the two can never drift.
 */
export const buildTeacherNoteDirectionLabel = (note: IUnifiedTeacherNote): string => {
  if (note.source === TEACHER_NOTE_SOURCE.RECEIVED) {
    return `From ${note.authorName}`;
  }

  const recipientSummary = formatRecipientSummary(note.recipients);
  if (note.isAuthoredByMe) {
    return recipientSummary ? `To ${recipientSummary}` : 'To —';
  }
  // An observing admin sees both ends of someone else's conversation.
  return `${note.authorName} → ${recipientSummary}`;
};
